// ============================================================
// MIDAS Action Definitions — execute functions for registered actions.
// Each entry maps an action_type to its execution logic.
// All API calls use the user's existing JWT via the app's API classes.
// ============================================================

import customerMachineServiceReportApi from "../../../customer/machinery/api/customerMachineServiceReportsApi";
import { registerAction } from "./actionRegistry";
import type { ActionSchema, ActionExecutionContext } from "./actionRegistry";
import type { ServiceReportFile } from "../../../customer/machinery/models/ServiceReportsModel";

// ============================================================
// Machine part GroupKey options for the affected_machine_part dropdown
// ============================================================

const MACHINE_PART_GROUP_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "spindleSystem", label: "Spindle System" },
  { value: "axisDrives", label: "Axis Drives" },
  { value: "cncControl", label: "CNC Control" },
  { value: "electrical", label: "Electrical" },
  { value: "hydraulicSystem", label: "Hydraulic System" },
  { value: "pneumaticSystem", label: "Pneumatic System" },
  { value: "coolantSystem", label: "Coolant System" },
  { value: "lubrication", label: "Lubrication" },
  { value: "toolChanger", label: "Tool Changer" },
  { value: "turretLathes", label: "Turret / Lathes" },
  { value: "workholding", label: "Workholding" },
  { value: "chipManagement", label: "Chip Management" },
  { value: "enclosureSafety", label: "Enclosure / Safety" },
  { value: "consumables", label: "Consumables" },
  { value: "other", label: "Other" },
];

// ============================================================
// Hardcoded schemas for MA-0a (until dynamic schema endpoint exists)
// ============================================================

export const CREATE_SERVICE_REPORT_SCHEMA: ActionSchema = {
  action_type: "machinery.create_service_report",
  module: "Machinery",
  required_permission: "Read",
  approval_tier: 1,
  display: {
    title: "Create Service Report",
    icon: "description",
  },
  fields: [
    {
      name: "title",
      type: "text",
      label: "Title",
      description: "Short title describing the issue.",
      required: true,
      max_length: 100,
      display_hints: { emphasis: "high" },
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      description: "Detailed description of the issue.",
      required: false,
      max_length: 4000,
      display_hints: { emphasis: "normal" },
    },
    {
      name: "notes",
      type: "textarea",
      label: "Technical Notes",
      description: "Technical diagnosis or additional notes.",
      required: false,
      max_length: 4000,
      display_hints: { emphasis: "normal" },
    },
    {
      name: "alarm_code",
      type: "text",
      label: "Alarm Code",
      description: "Machine alarm or error code.",
      required: false,
      max_length: 100,
      display_hints: { emphasis: "normal", group: "details" },
    },
    {
      name: "urgency",
      type: "dropdown",
      label: "Urgency",
      description: "How urgent is this issue?",
      required: false,
      options: [
        { value: "1", label: "Critical \u2014 Machine down" },
        { value: "2", label: "High \u2014 Limited operation" },
        { value: "4", label: "Low \u2014 Routine maintenance" },
      ],
      display_hints: { emphasis: "normal", group: "details" },
    },
    {
      name: "affected_machine_part",
      type: "dropdown",
      label: "Affected Machine Part",
      description: "Which machine system is affected?",
      required: false,
      options: MACHINE_PART_GROUP_OPTIONS,
      display_hints: { emphasis: "normal", group: "details" },
    },
    {
      name: "media",
      type: "file",
      label: "Photo / Document",
      description: "Attached photo or document from diagnosis.",
      required: false,
      accept: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
      max_size: 10 * 1024 * 1024,
      display_hints: { emphasis: "normal" },
    },
    {
      name: "created_at",
      type: "readonly",
      label: "Alarm Date",
      description: "When the alarm was diagnosed.",
      required: false,
      display_hints: { emphasis: "normal" },
    },
  ],
};

// ============================================================
// Hardcoded schema lookup — used as fallback when SSE schema is empty
// ============================================================

const HARDCODED_SCHEMAS: Record<string, ActionSchema> = {
  "machinery.create_service_report": CREATE_SERVICE_REPORT_SCHEMA,
};

/** Get hardcoded schema for an action type (fallback when SSE schema is empty) */
export const getHardcodedSchema = (actionType: string): ActionSchema | null => {
  return HARDCODED_SCHEMAS[actionType] || null;
};

// ============================================================
// GroupKey → MachineParts GUID resolver (BUG10-4, BUG10-14)
//
// Maps GroupKey to the first part's GUID in the Machinery DB
// MachineParts table. These are hardcoded because:
// 1. The MachineParts table uses stable seeded IDs
// 2. The Catalog service categories have DIFFERENT IDs that
//    violate the FK_ServiceReports_MachineParts constraint
// 3. No Machinery API endpoint exists for MachineParts
// ============================================================

/** Direct mapping from GroupKey to Machinery.MachineParts.Id (first part in each group) */
const GROUP_KEY_TO_MACHINE_PART_ID: Record<string, string> = {
  spindleSystem:  "a0000001-0001-0001-0001-000000000001", // Main spindle
  axisDrives:     "a0000002-0002-0002-0001-000000000001", // Servo motors
  cncControl:     "a0000003-0003-0003-0001-000000000001", // Controller / HMI
  electrical:     "a0000004-0004-0004-0001-000000000001", // Power supply
  hydraulicSystem:"a0000005-0005-0005-0001-000000000001", // Hydraulic pump
  pneumaticSystem:"a0000006-0006-0006-0001-000000000001", // Air system
  coolantSystem:  "a0000007-0007-0007-0001-000000000001", // Coolant pump
  lubrication:    "a0000008-0008-0008-0001-000000000001", // Lubrication pump
  toolChanger:    "a0000009-0009-0009-0001-000000000001", // ATC mechanism
  turretLathes:   "a000000a-000a-000a-0001-000000000001", // Turret mechanism
  workholding:    "a000000b-000b-000b-0001-000000000001", // Chuck / collet
  chipManagement: "a000000c-000c-000c-0001-000000000001", // Chip conveyor
  enclosureSafety:"a000000d-000d-000d-0001-000000000001", // Doors / guards
  consumables:    "a000000e-000e-000e-0001-000000000001", // Filters
  other:          "a000000f-000f-000f-0001-000000000001", // Other
};

/**
 * Resolve a GroupKey string to a MachineParts GUID.
 * Uses hardcoded IDs from the Machinery database MachineParts table.
 * Returns undefined if the GroupKey is not recognized (SR created without affected part).
 */
const resolveGroupKeyToGuid = (groupKey: string | undefined): string | undefined => {
  if (!groupKey || typeof groupKey !== "string") return undefined;
  return GROUP_KEY_TO_MACHINE_PART_ID[groupKey];
};

// ============================================================
// Media base64 → ServiceReportFile converter (BUG10-6)
// ============================================================

/** Convert bot-provided media object to ServiceReportFile for the API */
const convertMediaToFile = (media: any): ServiceReportFile | null => {
  if (!media || typeof media !== "object") return null;
  const { base64, filename, mime_type } = media;
  if (!base64 || typeof base64 !== "string") return null;

  // Calculate approximate file size from base64 length
  const size = Math.ceil((base64.length * 3) / 4);

  return {
    content: base64,
    name: filename || "attachment",
    size,
    typeCode: 1, // ReportDocument
  };
};

// ============================================================
// created_at → openDate parser (BUG10-8)
// ============================================================

/** Parse ISO timestamp to a Date object for the API openDate field */
const parseCreatedAt = (createdAt: string | undefined): Date | undefined => {
  if (!createdAt || typeof createdAt !== "string") return undefined;
  try {
    const d = new Date(createdAt);
    if (isNaN(d.getTime())) return undefined;
    return d;
  } catch {
    return undefined;
  }
};

// ============================================================
// Register: nav.switch_machine (tier 0, client-side only)
// Actual context-switching is handled by ChatContextSwitchButtons.
// These registrations exist for registry completeness (isRegisteredAction,
// getActionExecutor, canExecute checks).
// ============================================================

registerAction({
  type: "nav.switch_machine",
  module: "Navigation",
  requiredPermission: "Read",
  approvalTier: 0,
  execute: async () => {
    // Client-side only — actual switching is handled by ChatContextSwitchButtons
    return { success: true, message: "Switched to machine mode" };
  },
});

registerAction({
  type: "nav.switch_platform",
  module: "Navigation",
  requiredPermission: "Read",
  approvalTier: 0,
  execute: async () => {
    // Client-side only — actual switching is handled by ChatContextSwitchButtons
    return { success: true, message: "Switched to platform mode" };
  },
});

// ============================================================
// Register: machinery.create_service_report
// API: createServiceReport(model, machineId) — returns ServiceReport object
// (typed as Promise<string> but backend returns full object)
// ============================================================

registerAction({
  type: "machinery.create_service_report",
  module: "Machinery",
  requiredPermission: "Read",
  approvalTier: 1,
  execute: async (values, context?: ActionExecutionContext) => {
    // Use machineId from execution context (Redux machineContext) as primary source.
    // Falls back to values.machine_id (bot-provided, may be stripped by sanitizer).
    const machineId = context?.machineId || values.machine_id;
    if (!machineId) {
      return {
        success: false,
        message: "No machine selected. Please select a machine first.",
      };
    }

    // BUG10-4 + BUG10-14: Resolve GroupKey to MachineParts GUID (hardcoded — Catalog IDs differ from Machinery FK)
    const affectedPartCategoryId = resolveGroupKeyToGuid(values.affected_machine_part);

    // BUG10-6: Convert media base64 to ServiceReportFile
    const mediaFile = convertMediaToFile(values.media);
    const files: ServiceReportFile[] | null = mediaFile ? [mediaFile] : null;

    // BUG10-8: Parse created_at to openDate
    const openDate = parseCreatedAt(values.created_at);

    // BUG10-9: Parse urgency value to number
    const urgencyValue = values.urgency;
    const urgency = urgencyValue ? Number(urgencyValue) : undefined;
    const validUrgency = urgency === 1 || urgency === 2 || urgency === 4 ? urgency : undefined;

    const response = await customerMachineServiceReportApi.createServiceReport(
      {
        title: values.title || "",
        description: values.description || "",
        notes: values.notes || undefined,
        alarmCode: values.alarm_code || undefined,
        urgency: validUrgency,
        affectedPartCategoryId,
        openDate,
        files,
        // TODO: BUG10-5 — backend ChangeServiceReportViewModel needs AlarmCode, Urgency,
        // and CreatedViaMidas fields. Until Machinery Dev adds them, these may be silently
        // ignored by the backend. The frontend sends them proactively.
        createdViaMidas: true,
      },
      machineId
    );
    // API is typed as Promise<string> but backend returns ServiceReport object
    const reportNumber = typeof response === "object" && response !== null
      ? (response as any).number || (response as any).id || String(response)
      : String(response);
    return {
      success: true,
      entityNumber: reportNumber,
      message: `Service Report ${reportNumber} created`,
      link: `/machinery/${machineId}/service-reports/${reportNumber}`,
      data: { number: reportNumber },
    };
  },
});
